import { useState, useEffect, useCallback, useRef } from "react";
import { PaginatedResult } from "../types/api";

export type { PaginatedResult };

// Returns null on failure, matching the service-layer convention
export type PaginatedFetcher<T, F> = (
  page: number,
  filters: F
) => Promise<PaginatedResult<T> | null>;

/**
 * Adapt a service's `{ <plural>, total, page, limit }` response to the
 * PaginatedResult the hook wants. The services predate PaginatedResult and
 * each names its rows differently, so the rows are passed separately.
 */
export const pageFrom = <T>(
  items: T[],
  meta: { total: number; page: number; limit: number }
): PaginatedResult<T> => ({
  items,
  total: meta.total,
  page: meta.page,
  limit: meta.limit,
  lastPage: meta.limit > 0 ? Math.ceil(meta.total / meta.limit) : 0,
});

interface UsePaginatedResourceOptions<F> {
  // Skips fetching while false (e.g. an unresolved route param)
  enabled?: boolean;
  initialPage?: number;
  initialLimit?: number;
  initialFilters?: F;
  // When this changes, the list returns to page 1. Detail pages reuse one
  // component instance across records (/community/1 -> /community/2 does
  // not remount), so without it a stale page number carries over to the
  // next record and requests a page it may not have.
  resetKey?: string | number | null;
  errorMessage: string;
}

// Server-side pagination for any list backed by a Laravel paginator: the
// list pages, the search panels, and the lists embedded in detail pages.
//
// Seven hooks used to re-implement this state machine by hand and two of
// them got the dependency arrays wrong - see the note on fetchPage below.
export const usePaginatedResource = <T, F extends object>(
  fetcher: PaginatedFetcher<T, F>,
  {
    enabled = true,
    initialPage = 1,
    initialLimit = 10,
    initialFilters,
    resetKey,
    errorMessage,
  }: UsePaginatedResourceOptions<F>
) => {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [filters, setFilters] = useState<F>(initialFilters ?? ({} as F));

  // `limit` is excluded from the deps: setLimit runs inside the fetch.
  const fetchPage = useCallback(async () => {
    if (!enabled) {
      setItems([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetcher(page, filters);

      if (response) {
        setItems(response.items);
        setLimit(response.limit);
        setTotal(response.total);
      } else {
        setError(errorMessage);
        setItems([]);
        setTotal(0);
      }
    } catch (err) {
      setError(errorMessage);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [enabled, fetcher, page, filters, errorMessage]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  // Deliberately skips the first run: page already starts at initialPage.
  const seenResetKey = useRef(resetKey);
  useEffect(() => {
    if (seenResetKey.current !== resetKey) {
      seenResetKey.current = resetKey;
      setPage(1);
    }
  }, [resetKey]);

  const refresh = useCallback(() => {
    fetchPage();
  }, [fetchPage]);

  // Reset to page 1, or a stale page number returns an empty 200.
  const updateFilters = useCallback((newFilters: F) => {
    setFilters(newFilters);
    setPage(1);
  }, []);

  // Change filters but stay on the current page - for filter panels that
  // drive their own page reset.
  const setFiltersOnly = useCallback((newFilters: F) => {
    setFilters(newFilters);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({} as F);
    setPage(1);
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  return {
    items,
    // Escape hatch for optimistic updates (e.g. toggling a like without
    // refetching the page). Prefer refresh() for anything server-driven.
    setItems,
    loading,
    error,
    total,
    page,
    limit,
    filters,
    totalPages: limit > 0 ? Math.ceil(total / limit) : 0,
    refresh,
    updateFilters,
    setFiltersOnly,
    clearFilters,
    changePage,
  };
};
